import { Adresse } from "codegen";
import { format, isFuture, isValid, parse } from "date-fns";
import { useEffect, useState } from "react";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";
import StyledText from "@ntemarked/mittne-components/dist/StyledText/StyledText";
import TabSelect from "@ntemarked/mittne-components/dist/TabSelect/TabSelect";

import TextField from "components/organisms/form/TextField";
import { HttpError, useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { SearchButton, SearchMeterWrapper, SearchWrapper, TabSelectWrapper } from "pages/Strombestilling/orders/components/MeterSearch";
import { validateFodselsnummer } from "utils/Utils";

// TODO: Bytt til den genererte typen når personsøket er med i OpenAPI-specen til strømbestilling-api
export interface PersonSoketreff {
	ssn: string;
	fornavn?: string;
	etternavn?: string;
	adresse?: Adresse;
}

interface PersonSearchProps {
	/**
	 * Navn fra skjemaet, for skjema som allerede har navnefelt for personen (som kontaktpersonen i sikring). Uten navn
	 * viser personsøket egne navnefelt.
	 */
	firstName?: string;
	lastName?: string;
	fodselsnummer?: string;
	setFodselsnummer: (fodselsnummer: string | undefined) => void;
	/** Kalles ved treff, for skjema som vil fylle ut navnet fra Folkeregisteret */
	onPersonFound?: (person: PersonSoketreff) => void;
}

// Bare fødselsdatodelen vises. Det viktige er at saksbehandler ser at søket ga treff.
export const maskFodselsnummer = (fodselsnummer: string) => `${fodselsnummer.slice(0, 6)} *****`;

const formatPerson = (person: PersonSoketreff) =>
	`${[person.fornavn, person.etternavn].filter(Boolean).join(" ")}, fødselsnummer ${maskFodselsnummer(person.ssn)}` +
	(person.adresse?.asString ? `, ${person.adresse.asString}` : "");

/** Fødselsdato skrives som ddmmåååå, samme rekkefølge som i fødselsnummeret */
function parseFodselsdato(value: string): { date: Date | null; error: string | null } {
	if (!value) return { date: null, error: "Fyll ut fødselsdato." };
	if (value.length === 6) return { date: null, error: "Skriv årstallet med fire sifre: ddmmåååå." };
	const date = parse(value, "ddMMyyyy", new Date());
	if (value.length !== 8 || !isValid(date) || date.getFullYear() < 1900) {
		return { date: null, error: "Fødselsdato må skrives som ddmmåååå, for eksempel 15011990." };
	}
	if (isFuture(date)) return { date: null, error: "Fødselsdato kan ikke være frem i tid." };
	return { date, error: null };
}

export default function PersonSearch({ firstName, lastName, fodselsnummer, setFodselsnummer, onPersonFound }: Readonly<PersonSearchProps>) {
	const [isPersonSearch, setIsPersonSearch] = useState(false);
	const [fodselsdato, setFodselsdato] = useState("");
	const [person, setPerson] = useState<PersonSoketreff | null>(null);
	const [searchPersonMessage, setSearchPersonMessage] = useState<string | null>(null);
	const [isFodselsnummerTouched, setIsFodselsnummerTouched] = useState(false);
	const hasOwnNameFields = firstName === undefined || lastName === undefined;
	const [ownFirstName, setOwnFirstName] = useState("");
	const [ownLastName, setOwnLastName] = useState("");
	const searchFirstName = (hasOwnNameFields ? ownFirstName : firstName).trim();
	const searchLastName = (hasOwnNameFields ? ownLastName : lastName).trim();

	const { date: parsedFodselsdato, error: fodselsdatoError } = parseFodselsdato(fodselsdato);
	const fodselsdatoParam = parsedFodselsdato ? format(parsedFodselsdato, "yyyy-MM-dd") : "";

	const navn = `${searchFirstName} ${searchLastName}`;

	const { refetch: searchPerson, isFetching: isLoadingPerson } = useStrombestillingApiQuery<PersonSoketreff>(
		`/info/person/sok?navn=${encodeURIComponent(navn)}&foedselsdato=${fodselsdatoParam}`,
		["personsok", navn, fodselsdatoParam],
		false // Hent bare ved knappetrykk
	);

	// Et treff gjelder bare for navnet det ble søkt med
	useEffect(() => {
		if (isPersonSearch) resetSearch();
		// Om vi legger til resetSearch i dependency array vil treffet nullstilles ved hver rendring av forelderen
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navn]);

	function resetSearch() {
		setPerson(null);
		setSearchPersonMessage(null);
		setFodselsnummer(undefined);
	}

	async function handleSearch() {
		if (isLoadingPerson) return;
		resetSearch();

		if (!searchFirstName || !searchLastName) {
			setSearchPersonMessage("Fyll ut fornavn og etternavn før du søker.");
			return;
		}
		if (fodselsdatoError) {
			setSearchPersonMessage(fodselsdatoError);
			return;
		}

		try {
			const response = await searchPerson();
			// Folkeregisteret gir bare treff når søket identifiserer nøyaktig én person, ellers 404
			if (response.error instanceof HttpError && response.error.status === 404) {
				setSearchPersonMessage("Fant ingen entydig person med dette navnet og denne fødselsdatoen. Sjekk stavemåten, eller bruk fødselsnummer.");
				return;
			}
			if (response.error) throw response.error;
			if (response.data) {
				setPerson(response.data);
				setFodselsnummer(response.data.ssn);
				onPersonFound?.(response.data);
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			setSearchPersonMessage(`Noe gikk galt ved søk etter person, feilmelding: ${errorMessage}`);
		}
	}

	return (
		<SearchWrapper direction={Direction.column} gap="1rem">
			<TabSelectWrapper>
				<TabSelect
					options={[
						{ value: "false", label: "Fødselsnummer" },
						{ value: "true", label: "Personsøk" },
					]}
					value={isPersonSearch + ""}
					onChange={(v) => {
						// Fødselsdato og fane låses mens søket pågår, så svaret alltid gjelder det som står i skjemaet. TabSelect har ingen disabled
						if (isLoadingPerson) return;
						setIsPersonSearch(v === "true");
						resetSearch();
					}}
				/>
			</TabSelectWrapper>

			{!isPersonSearch ? (
				<div>
					<TextField
						label="Fødselsnummer"
						type="text"
						inputMode="numeric"
						maxLength={11}
						autoComplete="off"
						value={fodselsnummer ?? ""}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFodselsnummer(e.target.value.replace(/\D/g, "") || undefined)}
						onBlur={() => setIsFodselsnummerTouched(true)}
					/>
					{!!fodselsnummer && (isFodselsnummerTouched || fodselsnummer.length === 11) && !validateFodselsnummer(fodselsnummer) && (
						<StyledText style={{ fontWeight: "bold" }}>Ikke et gyldig fødselsnummer.</StyledText>
					)}
				</div>
			) : (
				<>
					{hasOwnNameFields && (
						<Flex gap="0.5rem">
							<TextField
								label="Fornavn"
								autoComplete="off"
								value={ownFirstName}
								disabled={isLoadingPerson}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOwnFirstName(e.target.value)}
							/>
							<TextField
								label="Etternavn"
								autoComplete="off"
								value={ownLastName}
								disabled={isLoadingPerson}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOwnLastName(e.target.value)}
							/>
						</Flex>
					)}
					<SearchMeterWrapper>
						<TextField
							label="Fødselsdato"
							description="Søker på fornavn, etternavn og fødselsdato. Skriv som ddmmåååå"
							placeholder="ddmmåååå"
							type="text"
							inputMode="numeric"
							autoComplete="off"
							value={fodselsdato}
							disabled={isLoadingPerson}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								// Punktum og andre skilletegn fjernes, så også 15.01.1990 kan limes inn. Derfor ingen maxLength, den ville kuttet innlimingen
								setFodselsdato(e.target.value.replace(/\D/g, "").slice(0, 8));
								resetSearch();
							}}
							onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleSearch();
								}
							}}
						/>
						<SearchButton
							label={isLoadingPerson ? "Søker..." : "Søk etter person"}
							type="button"
							loading={isLoadingPerson}
							onClick={handleSearch}
						/>
					</SearchMeterWrapper>

					{person && <p>Fant {formatPerson(person)}.</p>}
					{searchPersonMessage && <StyledText style={{ fontWeight: "bold" }}>{searchPersonMessage}</StyledText>}
				</>
			)}
		</SearchWrapper>
	);
}
